import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api'
import type { User, Role } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('pmp_token'))
  const loading = ref(false)

  const isAuthenticated = computed(() => Boolean(token.value && user.value))
  const role = computed(() => user.value?.role as Role | undefined)

  const ROLE_LEVELS: Record<Role, number> = {
    partner_client: 0,
    dexcom_support: 1,
    dexcom_management: 2,
    dexcom_director: 3,
    dexcom_admin: 4,
  }

  function hasRole(minRole: Role): boolean {
    if (!user.value) return false
    return (ROLE_LEVELS[user.value.role] ?? -1) >= ROLE_LEVELS[minRole]
  }

  function setToken(t: string) {
    token.value = t
    localStorage.setItem('pmp_token', t)
  }

  async function fetchUser() {
    if (!token.value) return
    loading.value = true
    try {
      user.value = await api.auth.me() as User
    } catch (err: any) {
      // If the error is access_pending, keep the token — the router guard will redirect to /pending
      // If any other error (401, etc.), clear the session
      if (err?.data?.code !== 'access_pending' && err?.data?.code !== 'access_suspended') {
        token.value = null
        user.value = null
        localStorage.removeItem('pmp_token')
      }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try { await api.auth.logout() } catch { /* ignore */ }
    token.value = null
    user.value = null
    localStorage.removeItem('pmp_token')
  }

  return { user, token, loading, isAuthenticated, role, hasRole, setToken, fetchUser, logout }
})
